import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useAuth } from '../../../hooks/use-auth';
import { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { wait } from '../../../utils/wait';
import { userApi } from '../../../api/user-api';
import { useNavigate } from 'react-router-dom';

export const AdminCreateForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const url = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}/api/role/get/`;
        const accessToken = localStorage.getItem('accessToken');
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setRoles(data.roles);
      } catch (error) {
        console.error('Error fetching roles:', error);
        toast.error('Erreur lors de la récupération des rôles.');
      }
    };
    fetchRoles();
  }, []);

  const formik = useFormik({
    initialValues: {
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      avatar: '',
      roles_id: '',
      submit: null,
    },
    validationSchema: Yup.object({
      firstname: Yup.string().max(255).required('Le prénom est requis'),
      lastname: Yup.string().max(255).required('Le nom est requis'),
      email: Yup.string().email('Email invalide').max(255).required("L'email est requis"),
      avatar: Yup.string().max(255),
      password: Yup.string().max(255).required('Le mot de passe est requis'),
      roles_id: Yup.string().required('Le rôle est requis'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        const response = await userApi.createAdmin(user.admin_id, values, 0, 0, values.roles_id);
        if (response.id) {
          toast.success('Admin créé avec succès !');
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
          helpers.resetForm();
        } else {
          toast.error(response.msg || "Erreur lors de la création de l'admin.");
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: response.msg });
        }
      } catch (err) {
        console.error(err);
        toast.error('Une erreur est survenue !');
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    },
  });

  const initials = formik.values.firstname
    ? formik.values.firstname.charAt(0).toUpperCase()
    : null;

  const avatarSrc = formik.values.avatar || null;

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>

        {/* Barre de couleur supérieure */}
        <Box sx={{ height: 6, background: 'linear-gradient(90deg, #6060B8 0%, #9090D0 100%)' }} />

        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 44, height: 44, borderRadius: '50%',
              backgroundColor: 'rgba(96,96,184,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <AdminPanelSettingsOutlinedIcon sx={{ color: '#6060B8', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Nouvel administrateur
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Remplissez les champs ci-dessous pour créer un administrateur
            </Typography>
          </Box>
          <Avatar
            src={avatarSrc}
            sx={{
              ml: 'auto', width: 44, height: 44,
              backgroundColor: initials ? '#6060B8' : '#9E9E9E',
              color: 'white', fontWeight: 700,
            }}
          >
            {!avatarSrc && initials}
          </Avatar>
        </Box>
        <Divider />

        {/* Champs du formulaire */}
        <CardContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prénom"
                name="firstname"
                required
                autoCapitalize="characters"
                value={formik.values.firstname}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.firstname && formik.errors.firstname)}
                helperText={formik.touched.firstname && formik.errors.firstname}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom"
                name="lastname"
                required
                autoCapitalize="characters"
                value={formik.values.lastname}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.lastname && formik.errors.lastname)}
                helperText={formik.touched.lastname && formik.errors.lastname}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                required
                value={formik.values.email}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.email && formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mot de passe"
                name="password"
                required
                type={showPassword ? 'text' : 'password'}
                value={formik.values.password}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.password && formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showPassword
                          ? <VisibilityOff sx={{ fontSize: 20, color: 'text.secondary' }} />
                          : <Visibility sx={{ fontSize: 20, color: 'text.secondary' }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL de l'avatar"
                name="avatar"
                value={formik.values.avatar}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.avatar && formik.errors.avatar)}
                helperText={formik.touched.avatar && formik.errors.avatar}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ImageOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={Boolean(formik.touched.roles_id && formik.errors.roles_id)}
              >
                <InputLabel id="role-label" required>Rôle</InputLabel>
                <Select
                  labelId="role-label"
                  label="Rôle"
                  name="roles_id"
                  value={formik.values.roles_id}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <BadgeOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} />
                    </InputAdornment>
                  }
                >
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.role_name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.roles_id && formik.errors.roles_id && (
                  <FormHelperText>{formik.errors.roles_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>

          </Grid>
        </CardContent>

        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 3, py: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            startIcon={<SaveOutlinedIcon />}
            sx={{ backgroundColor: '#6060B8', '&:hover': { backgroundColor: '#4a4a9a' } }}
          >
            Créer
          </Button>
        </Box>

      </Card>
    </form>
  );
};
